import { useRef, useState } from "react";
import type { ReactNode, TouchEvent } from "react";
import { RefreshCw } from "lucide-react";

const TRIGGER_DISTANCE = 52;
const MAX_DISTANCE = 78;

type PullToRefreshProps = {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  disabled?: boolean;
  className?: string;
};

function scrollTopFor(element: HTMLElement | null) {
  const adminShell = element?.closest<HTMLElement>(".admin-shell");
  if (adminShell) {
    return adminShell.scrollTop;
  }
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
}

function isInteractiveTarget(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest("button, a, input, select, textarea, [role='tab']"));
}

export function PullToRefresh({ children, onRefresh, disabled = false, className = "" }: PullToRefreshProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  function resetPull() {
    startYRef.current = null;
    distanceRef.current = 0;
    setDistance(0);
  }

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    if (disabled || refreshing || event.touches.length !== 1 || scrollTopFor(rootRef.current) > 0 || isInteractiveTarget(event.target)) {
      return;
    }
    startYRef.current = event.touches[0].clientY;
  }

  function handleTouchMove(event: TouchEvent<HTMLDivElement>) {
    if (startYRef.current === null || disabled || refreshing || scrollTopFor(rootRef.current) > 0 || event.touches.length !== 1) {
      return;
    }
    const delta = event.touches[0].clientY - startYRef.current;
    if (delta <= 0) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    const nextDistance = Math.min(MAX_DISTANCE, Math.round(delta * 0.45));
    distanceRef.current = nextDistance;
    setDistance(nextDistance);
  }

  function handleTouchEnd() {
    const shouldRefresh = distanceRef.current >= TRIGGER_DISTANCE && !disabled && !refreshing;
    startYRef.current = null;
    if (!shouldRefresh) {
      resetPull();
      return;
    }

    distanceRef.current = TRIGGER_DISTANCE;
    setDistance(TRIGGER_DISTANCE);
    setRefreshing(true);
    void Promise.resolve()
      .then(onRefresh)
      .catch(() => undefined)
      .finally(() => {
        setRefreshing(false);
        resetPull();
      });
  }

  return (
    <div
      ref={rootRef}
      className={`pull-refresh-root${className ? ` ${className}` : ""}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={resetPull}
    >
      <div
        className={`pull-refresh-indicator${refreshing ? " is-refreshing" : ""}`}
        style={{ height: distance, opacity: distance > 0 ? 1 : 0 }}
        aria-live="polite"
        aria-hidden={distance === 0}
      >
        <RefreshCw size={15} />
        <span>{refreshing ? "正在更新" : distance >= TRIGGER_DISTANCE ? "松开刷新" : "下拉刷新"}</span>
      </div>
      {children}
    </div>
  );
}
